import React from "react";
import {
  Box,
  Heading,
  Text,
  Input,
  VStack,
  HStack,
  Button,
  IconButton,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";

const SectionBox = ({ title, children }) => (
  <Box
    mt={6}
    p={4}
    border="2px solid"
    borderColor="gray.600"
    borderRadius="md"
    bg="white"
    boxShadow="md"
    textAlign="left"
  >
    <Heading size="md" mb={2}>{title}</Heading>
    <Divider mb={4} />
    {children}
  </Box>
);

const getBorderColor = (original, value) => {
  const hasOriginal = original !== undefined && original !== null && original !== "";
  const hasValue = value !== undefined && value !== null && value !== "";

  if (!hasOriginal && hasValue) return "2px solid green"; // Added
  if (hasOriginal && !hasValue) return "2px solid red";   // Deleted
  if (hasOriginal && hasValue && original !== value) return "2px solid blue"; // Changed
  return "1px solid #E2E8F0"; // Unchanged
};

const getTooltipLabel = (original, value) => {
  const hasOriginal = original !== undefined && original !== null && original !== "";
  const hasValue = value !== undefined && value !== null && value !== "";

  if (!hasOriginal && hasValue) return "Added new value";
  if (hasOriginal && !hasValue) return `Deleted previous value: ${original}`;
  if (hasOriginal && hasValue && original !== value) return `Changed from: ${original}`;
  return "";
};

const ShippingSection = ({
  shippingAddresses,
  updateShippingAddress,
  deleteShippingAddress,
  addShippingAddress,
  setShippingAddresses,
  facilityAddress,
  originalData = {},
}) => {
  const originalAddresses = originalData.shippingAddresses || [];

  return (
    <SectionBox title="Shipping Addresses">
      <Text fontSize="sm" color="gray.600" mb={3}>
        Please include all shipping addresses associated with the “Covered Entity”.
      </Text>

      {shippingAddresses.map((address, index) => {
        const original = originalAddresses[index] || {};

        const renderInput = (label, fieldName, value, onChange) => (
          <Box>
            <Text fontSize="sm" mb={1}>{label}</Text>
            <Tooltip label={getTooltipLabel(original[fieldName], value)} isDisabled={!getTooltipLabel(original[fieldName], value)}>
              <Input
                placeholder={label}
                value={value || ""}
                onChange={onChange}
                border={getBorderColor(original[fieldName], value)}
              />
            </Tooltip>
          </Box>
        );

        return (
          <Box
            key={index}
            position="relative"
            border="1px solid"
            borderColor="gray.500"
            p={4}
            mt={3}
            borderRadius="md"
          >
            {index > 0 && (
              <IconButton
                icon={<CloseIcon />}
                aria-label="Remove address"
                size="sm"
                position="absolute"
                top="-10px"
                right="-10px"
                zIndex="10"
                bg="#D62828"
                color="white"
                boxShadow="md"
                _hover={{ bg: "gray.100" }}
                onClick={() => deleteShippingAddress(index)}
              />
            )}

            <VStack spacing={2} mt={2} align="stretch">
              {renderInput("Street Address", "street", address.street, (e) =>
                updateShippingAddress(index, "street", e.target.value)
              )}
              {renderInput("City", "city", address.city, (e) =>
                updateShippingAddress(index, "city", e.target.value)
              )}
              {renderInput("State", "state", address.state, (e) =>
                updateShippingAddress(index, "state", e.target.value)
              )}
              {renderInput("Zip Code", "zip", address.zip, (e) =>
                updateShippingAddress(index, "zip", e.target.value)
              )}
            </VStack>

            {index === 0 && (
              <Button
                mt={2}
                size="sm"
                onClick={() =>
                  setShippingAddresses(
                    shippingAddresses.map((addr, i) =>
                      i === index ? facilityAddress : addr
                    )
                  )
                }
              >
                Same as Facility Address
              </Button>
            )}
          </Box>
        );
      })}

      {shippingAddresses.length < 50 && (
        <Button
          mt={3}
          bg="#2A9D8F"
          color="white"
          _hover={{ bg: "#21867a" }}
          onClick={addShippingAddress}
        >
          Add Another Address
        </Button>
      )}
    </SectionBox>
  );
};

export default ShippingSection;
